// Used for checking auth assignments vs requirements on api and client
// Use `hasPermission` to check assignments vs requirements

// Basic OR logic for auth check of permissions vs requirements
// Params:
//  assignments - array of assignments
//  requirement - a single requirement
function hasPermissionCheck (assignments, requirement) {
  // Early return if splat
  if (requirement === '*') {
    return assignments.length > 0
  }

  // Filter for seeing if a permission assingment meets a requirement
  const filter = assignment => {
    return requirement.slice(-2) === '.*'
      ? assignment.startsWith(requirement.slice(0, -1))
      : assignment === requirement
  }

  return assignments.some(assignment => filter(assignment))
}

// Based on the given requirements, find the correct assignments in scope and the correct requirements to do a basic intersection check
function getBasePermissionsAndRequirements (assignments, requirements) {
  if (typeof requirements === 'string') {
    return { assignments, requirements: [requirements] }
  } else if (Array.isArray(requirements)) {
    return { assignments, requirements }
  } else if (requirements && typeof requirements === 'object') {
    const requirementParts = Object.keys(requirements).map(key => {
      const foundAssignments = assignments[key] || []

      return getBasePermissionsAndRequirements(foundAssignments, requirements[key])
    })

    return [].concat(...requirementParts)
  }
}

// Given an array of permission assignments and requirements, return whether the assignments grant sufficient access as per the requirements
// Assignments must be a flat array of user permissions strings
// Requirements can be a single permission string, array of OR permissions or have mix of user/facility permissions
// Any permission requirement ending in .* will match assigned permissions beginning with the given requirement ending in .
// Examples:
//   'admin.search.view': User has 'admin.search.view'
//   ['reports.cc.*', 'admin.search.view']: User has anything that starts with 'reports.cc.' **OR** 'admin.search.view'
//   '*': User has any assignments
//   { user: 'admin.search.view' }: User has 'admin.search.view'
//   { facility: { 205: ['admin.search.view'] } }: User has 'admin.search.view' at facility with global id 205
//   { user: 'admin.search.user', facility: { 205: ['admin.search.facility'] } }: User has 'admin.search.user' or 'admin.search.facility' at facility with global id 205
function hasPermission (assignments, requirements) {
  // Filter for seeing if requirement is satisfied
  const filter = (assignments, requirement) => hasPermissionCheck(assignments, requirement)

  // If requirements is an array or string, ensure it checks the user scope
  if (Array.isArray(requirements)) {
    requirements = { user: requirements }
  } else if (typeof requirements === 'string') {
    requirements = { user: [requirements] }
  }

  // Get base list of requirements and assignments
  const checkPairs = getBasePermissionsAndRequirements(assignments, requirements)
  const hasAccess = checkPairs.length === 0 || checkPairs.some(pair => {
    return pair.requirements.length === 0 || pair.requirements.some(requirement => filter(pair.assignments, requirement))
  })

  return hasAccess
}

module.exports = {
  hasPermission,
  getBasePermissionsAndRequirements,
  hasPermissionCheck
}
